'use client';

// Interactive pricing calculator. Customer enters monthly RPC call
// volume + an average-cost-per-call estimate; we pick the smallest
// tier that covers the resulting monthly CC need and surface the
// effective rate per million CC. No hidden math: every line shows
// the arithmetic so a prospect can spot-check.
//
// Defaults are chosen for "typical wallet/explorer mix" — mostly
// reads (getblockcount, getrawtransaction, scripthash subscribes)
// at ~250 CC mean. Customers serving heavy chaingraph queries can
// dial up the CC/call slider to see how it compresses the tier
// recommendation.

import { useMemo, useState } from 'react';
import { TIERS, bundleRatePerCc, type TierName } from '@solidpeer/shared/pricing';

const VISIBLE_TIERS: TierName[] = ['hobby', 'build', 'scale', 'business'];

const PRESET_PROFILES = [
  { label: 'Light wallet',    calls_per_month:    50_000, cc_per_call: 200, blurb: 'Personal wallet, low traffic.' },
  { label: 'Production app',  calls_per_month: 1_000_000, cc_per_call: 250, blurb: 'Mid-volume product, balanced read/write.' },
  { label: 'Heavy indexer',   calls_per_month: 5_000_000, cc_per_call: 400, blurb: 'Chaingraph-heavy, frequent reorgs.' },
  { label: 'Power user',      calls_per_month:   500_000, cc_per_call: 800, blurb: 'Deep getblock with transactions.' },
] as const;

export function PriceCalculator() {
  const [calls,  setCalls]  = useState<number>(1_000_000);
  const [cc,     setCc]     = useState<number>(250);
  const [annual, setAnnual] = useState<boolean>(false);

  const total_cc_monthly = calls * cc;

  // Pick the cheapest tier that covers the monthly CC need.
  const recommendation = useMemo(() => {
    for (const t of VISIBLE_TIERS) {
      if (TIERS[t].cc_quota_monthly >= total_cc_monthly) {
        return t;
      }
    }
    return VISIBLE_TIERS[VISIBLE_TIERS.length - 1]!;
  }, [total_cc_monthly]);

  const term = annual ? 'annual' : 'monthly';
  const tier_def = TIERS[recommendation];
  const rate_per_cc = bundleRatePerCc(recommendation, term);
  const monthly_price = annual
    ? tier_def.monthly_price_usd * 10  // ~17% discount for paying annually
    : tier_def.monthly_price_usd;
  const overage_cc = Math.max(0, total_cc_monthly - tier_def.cc_quota_monthly);
  const overage_cost = overage_cc * rate_per_cc;
  const total_monthly = (annual ? monthly_price / 12 : monthly_price) + overage_cost;

  return (
    <section className="card space-y-6">
      <div>
        <h2 className="heading-2">Estimate your bill.</h2>
        <p className="mt-2 text-sm text-ink-dim max-w-2xl">
          Plug in your expected monthly call volume and average cost per call. We pick the
          smallest tier that covers it; everything beyond the tier quota bills at the same
          $/M rate (no separate overage table).
        </p>
      </div>

      {/* Profile presets */}
      <div>
        <div className="text-xs uppercase tracking-wider text-ink-faint mb-2">Profiles</div>
        <div className="flex flex-wrap gap-2">
          {PRESET_PROFILES.map((p) => (
            <button
              key={p.label}
              onClick={() => { setCalls(p.calls_per_month); setCc(p.cc_per_call); }}
              title={p.blurb}
              className="px-3 py-1.5 text-xs rounded-md ring-1 ring-inset ring-white/10 text-ink-dim hover:bg-surface-2 transition"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block">
          <div className="text-xs uppercase tracking-wider text-ink-faint">Calls per month</div>
          <div className="text-xs text-ink-faint mt-0.5">Total RPC requests across all your endpoints.</div>
          <input
            type="number" min={0} step={10000}
            value={calls}
            onChange={(e) => setCalls(Math.max(0, Number(e.target.value) || 0))}
            className="mt-2 w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 text-sm text-ink font-mono focus:outline-none focus:ring-accent/50"
          />
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-wider text-ink-faint">Average CC per call</div>
          <div className="text-xs text-ink-faint mt-0.5">Cheapest method = 100. Mixed wallet workloads ≈ 200-300.</div>
          <input
            type="number" min={100} step={10}
            value={cc}
            onChange={(e) => setCc(Math.max(100, Number(e.target.value) || 100))}
            className="mt-2 w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 text-sm text-ink font-mono focus:outline-none focus:ring-accent/50"
          />
        </label>
      </div>

      {/* Term toggle */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-ink-faint">Term:</span>
        <button
          onClick={() => setAnnual(false)}
          className={`px-3 py-1 text-xs rounded-md ring-1 ring-inset transition ${
            !annual ? 'bg-accent/15 text-accent ring-accent/30' : 'text-ink-dim ring-white/10 hover:bg-surface-2'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setAnnual(true)}
          className={`px-3 py-1 text-xs rounded-md ring-1 ring-inset transition ${
            annual ? 'bg-accent/15 text-accent ring-accent/30' : 'text-ink-dim ring-white/10 hover:bg-surface-2'
          }`}
        >
          Annual <span className="text-ink-faint">(~17% off)</span>
        </button>
      </div>

      {/* Result */}
      <div className="rounded-xl bg-gradient-to-br from-accent/10 via-accent/5 to-transparent ring-1 ring-inset ring-accent/30 p-6">
        <div className="text-xs uppercase tracking-wider text-accent">Recommendation</div>
        <div className="mt-2 text-2xl font-semibold capitalize">{recommendation} tier</div>
        <dl className="mt-4 space-y-2 text-sm">
          <Row k="Monthly CC needed" v={fmtN(total_cc_monthly)} />
          <Row k={`Tier quota (${recommendation})`} v={fmtN(tier_def.cc_quota_monthly)} />
          {overage_cc > 0 && (
            <Row k="Overage CC" v={`${fmtN(overage_cc)} @ $${rate_per_cc.toFixed(6)}/CC`} />
          )}
          <Row k={annual ? 'Annual base' : 'Monthly base'} v={`$${monthly_price.toFixed(2)}`} />
          {overage_cost > 0 && (
            <Row k="Monthly overage" v={`$${overage_cost.toFixed(2)}`} />
          )}
          <hr className="border-white/10 my-2" />
          <Row k="Effective monthly cost" v={`$${total_monthly.toFixed(2)}`} highlight />
          <Row k="Effective $/M CC" v={`$${(total_monthly / (total_cc_monthly / 1_000_000)).toFixed(4)}`} />
        </dl>
      </div>

      <p className="text-xs text-ink-faint">
        These numbers use the public per-method weights. Your actual mix may differ — the
        per-call CC charge varies by method. See <span className="font-mono">/methods</span> for the full table.
      </p>
    </section>
  );
}

function Row({ k, v, highlight }: { k: string; v: string; highlight?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${highlight ? 'text-ink' : 'text-ink-dim'}`}>
      <dt className="text-xs">{k}</dt>
      <dd className={`font-mono ${highlight ? 'text-base font-semibold' : 'text-xs'}`}>{v}</dd>
    </div>
  );
}

function fmtN(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2).replace(/\.?0+$/, '')}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toString();
}
